import _ from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { Toast, Loader } from '@vzmi/shp_shared_compo'
import { ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'
import styles from './PageViewsDaily.css'
import safeAwait from '../../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../../lib/errorHelper'
import withConnectedExport from '../../../../../containers/Common/WithConnectedExport'
import {
  DATE_FORMAT,
  TIME_DURATION,
  getAPIFormatDate,
  getRangeStringForUI,
  getFormatData
} from '../../../../../lib/MomentHelper'
import SPBaseModule from '../../../../../lib/SPBaseModule'
import { numeralFormat, getDefaultTimezoneMoment } from '../../../../../../lib/renderUtil'
import Block from '../../../../Common/NewDashboard/Block/Block'
import NoData from '../../../../Common/NoData/NoData'
import SummaryBanner from '../../../../Common/NewDashboard/Chart/SummaryBanner/SummaryBanner'
import SearchForm, {
  FIELD as SEARCH_FIELD,
  CONFIG as SEARCH_CONFIG
} from './SearchForm/SearchForm'
import ViewsChart, {
  FIELD as CHART_FIELD,
  TOOLTIP_DETAIL_LIST,
  TOOLTIP_DETAIL,
  SELLER_TOOLTIP_DETAIL
} from './ViewsChart/ViewsChart'
import { LISTING_TYPE } from '../../constants/listing'
import {
  CARRIER,
  CARRIER_MAP
} from '../../constants/carrier'
import {
  LISTING_ID_NOT_FOUND,
  NOT_FOUND
} from '../../constants/noData'
import { GA_CLICK_ACTION } from '../PageViewsAnalysis.ga'
import { transformPageViewsListToChartDataList } from '../../Shared'
import { getSellerSaleStateMapByTimeDuration } from '../../../../Seller/Dashboard/PageViewsAnalysis/helper'
import reminderStyles from '../../../DashboardReminder.css'

const DEFAULT_TIME_DURATION = TIME_DURATION.DAY.value

export const ALL_CARRIERS = [
  CARRIER.APP,
  CARRIER.MOBILE_WEB,
  CARRIER.DESKTOP_WEB,
  CARRIER.UNKNOWN
]

export const getNoDataDescription = (formValues, chartDataList) => {
  const listingId = _.toString(_.get(formValues, SEARCH_FIELD.LISTING_ID, ''))
  if (_.isEmpty(chartDataList)) {
    if (listingId) {
      return LISTING_ID_NOT_FOUND
    }
    return NOT_FOUND
  }
  return ''
}

export const getPageViewsParamsList = formValues => {
  const payloads = []
  const {
    mainRange,
    comparisonRange,
    ...restFormValues
  } = formValues
  const mainPayload = { mainRange, ...restFormValues }
  const comparisonPayload = _.isEmpty(comparisonRange)
    ? {}
    : { comparisonRange, ...restFormValues }

  // 先算主要的瀏覽量來源再算比較的瀏覽量來源
  for (const newFormValues of [mainPayload, comparisonPayload]) {
    if (_.isEmpty(newFormValues)) {
      continue
    }
    const payload = {}

    for (const field of _.keys(newFormValues)) {
      const fieldValue = newFormValues[field]
      switch (field) {
        case SEARCH_FIELD.MAIN_RANGE:
        case SEARCH_FIELD.COMPARISON_RANGE: {
          if (fieldValue) {
            const [
              reportDateStart,
              reportDateEnd
            ] = _.map(fieldValue, getAPIFormatDate)
            _.set(payload, 'reportDateStart', reportDateStart)
            _.set(payload, 'reportDateEnd', reportDateEnd)
          }
          break
        }
        case SEARCH_FIELD.LISTING_TYPE: {
          if (fieldValue === LISTING_TYPE.ORDER) {
            const fieldKey = SEARCH_FIELD.LISTING_ID
            _.set(payload, fieldKey, newFormValues[fieldKey])
          }
          break
        }
        case SEARCH_FIELD.CARRIERS: {
          const isAllCarrierExist = _.includes(fieldValue, CARRIER.ALL)
          if (isAllCarrierExist) {
            _.set(payload, field, ALL_CARRIERS)
            break
          }
          const fieldValueWithoutAll = _.filter(fieldValue, carrier => carrier !== CARRIER.ALL)
          _.set(payload, field, fieldValueWithoutAll)
          break
        }
        default:
          break
      }
    }

    payloads.push(payload)
  }

  return payloads
}

const SALE_STATE_FIELD = {
  NEW_ORDER: 'newOrder',
  REFUND_ORDER: 'refundOrder'
}

/**
 * 取得銷售概況 API 的 params
 * @param {Object} formValues - 表單值
 * @param {string} field - 表單欄位賣家
 * @returns {Object} 銷售概況 API 的 params
 */
export const getSaleStateParams = (formValues, field) => {
  const reportDates = _.map(
    _.get(formValues, field, []),
    date => getFormatData(date, DATE_FORMAT.API)
  )
  if (_.isEmpty(reportDates)) {
    return {}
  }

  const [reportDateStart, reportDateEnd] = reportDates
  const fields = [SALE_STATE_FIELD.NEW_ORDER, SALE_STATE_FIELD.REFUND_ORDER].map(field => `+${field}`)
  const saleStateParams = { reportDateStart, reportDateEnd, fields }
  return saleStateParams
}

/**
 * 將銷售概況按照時間區間分群
 */
export const getSaleStatsMap = (convertedSaleStats, timeDuration) => {
  let saleStatsMap = {}
  switch (timeDuration) {
    case TIME_DURATION.DAY.value:
      saleStatsMap = _.keyBy(convertedSaleStats, 'date')
      break
    case TIME_DURATION.WEEK.value: {
      // 按周分群時一週只放 6 天
      let startDate = _.get(convertedSaleStats, '0.date')
      let range = [moment(startDate), moment(startDate).add(5, 'days')]
      saleStatsMap = _.flow(
        saleStats => _.groupBy(saleStats, saleStat => {
          const currentDate = _.get(saleStat, 'date')
          const [start, end] = range
          if (
            start.isSameOrBefore(currentDate) &&
            end.isSameOrAfter(currentDate)
          ) {
            return startDate
          }
          startDate = moment(startDate).add(6, 'days').format(DATE_FORMAT.API)
          range = [
            moment(startDate),
            moment(startDate).add(5, 'days')
          ]
          return startDate
        }),
        getSaleStateSummaryMap
      )(convertedSaleStats)
      break
    }
    case TIME_DURATION.MONTH.value:
      saleStatsMap = _.flow(
        saleStats => _.groupBy(saleStats, saleStat => {
          const startDate = _.get(saleStat, 'date')
          return moment(startDate).format(DATE_FORMAT.API_START_DATE)
        }),
        getSaleStateSummaryMap
      )(convertedSaleStats)
      break
    default:
      break
  }
  return saleStatsMap
}

/**
 * 加總要顯示在圖表上的銷售概況
 */
export const getSaleStateSummaryMap = saleStatsMap => {
  const saleStateSummaryMap = _.reduce(
    saleStatsMap,
    (summaryByTime, saleStates, timeDuration) => {
      const currentTimeSaleStateSummary = {}
      for (const saleState of saleStates) {
        // 只加總圖表會用到的欄位
        for (const key of TOOLTIP_DETAIL_LIST) {
          currentTimeSaleStateSummary[key] = currentTimeSaleStateSummary[key] || 0
          currentTimeSaleStateSummary[key] += +_.get(saleState, key, 0)
        }
      }
      summaryByTime[timeDuration] = currentTimeSaleStateSummary
      return summaryByTime
    },
    {}
  )
  return saleStateSummaryMap
}

/**
 * 將銷售概況 API 回傳結果轉換為以圖表時間區間為 key 的對應表
 */
export const getSaleStatsMapByTimeDuration = (saleStateResp, isSeller) => {
  const convertedSaleStats = _.flow(
    // 從 api resp 取得銷售概況
    () => {
      return _.map([SALE_STATE_FIELD.NEW_ORDER, SALE_STATE_FIELD.REFUND_ORDER], field => {
        return _.get(saleStateResp, `${field}.statistics`, [])
      })
    },
    ([newOrderList, refundOrderList]) => {
      const convertedList = []
      for (const [index, newOrder = {}] of newOrderList.entries()) {
        const date = _.get(newOrder, 'reportDate')
        const refundOrder = _.get(refundOrderList, index, {})
        const stats = {
          date,
          [TOOLTIP_DETAIL.ORDER_QUANTITY]: +_.get(newOrder, 'quantity', 0),
          [TOOLTIP_DETAIL.ORDER_AMOUNT]: +_.get(newOrder, 'amount', 0),
          [TOOLTIP_DETAIL.REFUND_QUANTITY]: +_.get(refundOrder, 'quantity', 0),
          [TOOLTIP_DETAIL.REFUND_AMOUNT]: +_.get(refundOrder, 'amount', 0),
          ...(isSeller
            ? {
              [SELLER_TOOLTIP_DETAIL.SOLD_QUANTITY]: +_.get(newOrder, SELLER_TOOLTIP_DETAIL.SOLD_QUANTITY, 0),
              [SELLER_TOOLTIP_DETAIL.UNIQUE_CART_QUANTITY]: +_.get(newOrder, SELLER_TOOLTIP_DETAIL.UNIQUE_CART_QUANTITY, 0)
            }
            : {})
        }
        convertedList.push(stats)
      }
      return convertedList
    }
  )()
  // 按照圖表可能使用的時間區間將銷售概況轉換成對應表
  const saleStatsMapByTimeDuration = _.reduce(
    [TIME_DURATION.DAY, TIME_DURATION.WEEK, TIME_DURATION.MONTH],
    (saleStatsMap, timeDuration) => {
      const currentTimeDuration = timeDuration.value
      saleStatsMap[currentTimeDuration] = getSaleStatsMap(
        convertedSaleStats,
        currentTimeDuration
      )
      return saleStatsMap
    },
    {}
  )
  return saleStatsMapByTimeDuration
}

export const getViewsSummariesList = (pageViewsList, formValues, isSeller) => {
  const {
    mainRange,
    comparisonRange
  } = formValues
  const rangeList = [mainRange, comparisonRange]
  const isComparison = !_.isEmpty(comparisonRange)
  const viewsSummariesList = _.reduce(pageViewsList, (calculatedData, pageViews, index) => {
    const totalPageView = _.sum(
      _.map(pageViews, pageView => _.get(pageView, CHART_FIELD.PAGE_VIEW, 0))
    )
    const totalViewTime = _.sum(
      _.map(pageViews, pageView => _.get(pageView, CHART_FIELD.VIEW_TIME, 0))
    )
    const summaries = [{
      label: '總瀏覽量',
      value: numeralFormat(totalPageView, { allowZero: true })
    }, ...(isSeller
      ? [{
        label: '總停留時間(秒)',
        value: numeralFormat(totalViewTime, { allowZero: false })
      }]
      : [])]
    let title = ''
    if (isComparison) {
      const currentRange = rangeList[index]
      title = getRangeStringForUI(currentRange)
    }
    calculatedData.push({ title, summaries })
    return calculatedData
  }, [])
  return viewsSummariesList
}

export const transformToSheetData = (sheetData, listingId) => _.flow(
  data => data.reduce((acc, obj) => {
    const key = `${obj.reportDate}-${CARRIER_MAP[obj.carrier]}`
    if (!acc[key]) {
      // replace carrier with desc wording
      // also adding listingId for sheet
      acc[key] = {
        ...obj,
        carrier: CARRIER_MAP[obj.carrier],
        listingId
      }
    } else {
      // pageView summing by carrier
      acc[key].pageView += obj.pageView
    }
    return acc
  }, {}),
  _.flatMap
)(sheetData)

class PageViewsDaily extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      isComparisonChart: false,
      timeDuration: DEFAULT_TIME_DURATION,
      pageViewsList: [],
      viewsSummariesList: [],
      chartDataList: [],
      noDataDescription: NOT_FOUND,
      researchedFormData: {},
      isComparisonCancelled: false,
      isDailyDetailLoaded: false,
      isDetailVisible: false,
      saleStateMapByTimeDuration: {}
    }
    this.searchFormProps = null
  }

  componentDidMount () {
    this.searchFormProps.handleSubmit(this.onSearch)()
  }

  setSearchFormProps = formProps => (this.searchFormProps = formProps)

  onSearch = values => this.loading(async () => {
    const { fetchAllListingPageViews, isSeller } = this.props
    const { isComparisonCancelled } = this.state
    const { comparisonRange } = values
    const paramsList = getPageViewsParamsList({
      ...values,
      comparisonRange: isComparisonCancelled ? null : comparisonRange
    })
    const [reqError, respList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (reqError) {
      const error = _.get(reqError, 'payload.errorData.error[0]')
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    const pageViewsList = _.map(respList, resp => _.get(resp, 'data', []))
    // 查詢時賣場選擇全部賣場 & 裝置只有勾選全部時 tooltip 才顯示詳細資訊
    // 除了購中店可以在選擇單一裝置時顯示當日瀏覽資訊 [APACSMDESK-79619]
    const isAllCarrierSelected = _.isEqual(
      _.get(values, `${SEARCH_FIELD.CARRIERS}`),
      [CARRIER.ALL]
    )
    const isOneCarrierSelected = !isAllCarrierSelected && _.size(_.get(values, `${SEARCH_FIELD.CARRIERS}`)) === 1
    const isDetailVisible = (
      _.get(values, SEARCH_FIELD.LISTING_TYPE) === LISTING_TYPE.ALL &&
      (isAllCarrierSelected ||
      (isSeller && isOneCarrierSelected)
      )
    )
    if (isDetailVisible) {
      // 因為 fetchAllListingPageViews 是分月打的會產生多個請求
      // 為了避免同時太多請求導致阻塞，業績與退回待主要資料取得後才發出請求
      this.updateDailyDetail(values, pageViewsList)
    }

    const formValues = {
      ...values,
      timeDuration: DEFAULT_TIME_DURATION
    }
    const viewsSummariesList = getViewsSummariesList(pageViewsList, formValues, isSeller)
    const chartDataList = transformPageViewsListToChartDataList(pageViewsList, formValues)
    const noDataDescription = getNoDataDescription(values, chartDataList)
    const isComparisonChart = !_.isEmpty(values[SEARCH_FIELD.COMPARISON_RANGE])
    this.setState({
      pageViewsList,
      viewsSummariesList,
      chartDataList,
      researchedFormData: values,
      timeDuration: DEFAULT_TIME_DURATION,
      isComparisonChart,
      noDataDescription,
      isDetailVisible,
      isOneCarrierSelected
    })
    this.setIsSuccess(true)
  })

  updateDailyDetail = async (values, pageViewsList) => {
    this.setState({
      isDailyDetailLoaded: false,
      saleStateMapByTimeDuration: {}
    })

    const { fetchSaleStatsByDate, isSeller } = this.props
    const saleStateParams = getSaleStateParams(values, SEARCH_FIELD.MAIN_RANGE)
    const comparisonSaleStateParams = getSaleStateParams(values, SEARCH_FIELD.COMPARISON_RANGE)
    const [reqError, results] = await safeAwait(
      Promise.all(
        _.isEmpty(comparisonSaleStateParams)
          ? [fetchSaleStatsByDate(saleStateParams).then(httpHandler)]
          : [
            fetchSaleStatsByDate(saleStateParams).then(httpHandler),
            fetchSaleStatsByDate(comparisonSaleStateParams).then(httpHandler)
          ]
      )
    )
    const saleStateResp = _.get(results, '0', {})
    const comparisonSaleStateResp = _.get(results, '1', {})
    if (reqError) {
      const errorMessage = errorHelper.parseErrorMessage(reqError)
      Toast.error(`日瀏覽量銷售狀況取得失敗，${errorMessage}`)
      this.setState({ isDailyDetailLoaded: true })
      return
    }

    const saleStateMapByTimeDuration = getSaleStatsMapByTimeDuration(saleStateResp, isSeller)
    const comparisonSaleStateMapByTimeDuration = getSaleStatsMapByTimeDuration(comparisonSaleStateResp, isSeller)
    const totalSaleStateMapByTimeDuration = _.merge(
      saleStateMapByTimeDuration,
      comparisonSaleStateMapByTimeDuration
    )
    // 賣家銷售概況需要額外從 pageViewsList 組成 addCartUserQuantity 等欄位
    const pageViewData = _.get(pageViewsList, '0', [])
    const sellerSaleStateMapByTimeDuration = getSellerSaleStateMapByTimeDuration(pageViewData, totalSaleStateMapByTimeDuration)

    this.setState({
      isDailyDetailLoaded: true,
      saleStateMapByTimeDuration: isSeller ? sellerSaleStateMapByTimeDuration : totalSaleStateMapByTimeDuration
    })
  }

  onTimeDurationChange = event => {
    const {
      pageViewsList,
      researchedFormData
    } = this.state
    const currentTimeDuration = event.target.value
    const formValues = {
      ...researchedFormData,
      timeDuration: currentTimeDuration
    }
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.DAILY_CHART_GROUP,
      payload: currentTimeDuration
    })
    const chartDataList = transformPageViewsListToChartDataList(pageViewsList, formValues)
    this.setState({
      timeDuration: currentTimeDuration,
      chartDataList
    })
  }

  onCollapseChange = (isOpen, e) => {
    this.setState({
      isComparisonCancelled: !!isOpen
    })
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.DAILY_COMPARISON_RANGE
    })
  }

  onListingChange = (e, value) => {
    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.DAILY_LISTING_OPTION,
      payload: value
    })
  }

  onCarriersChange = (e, nextCarriers, prevCarriers) => {
    // 勾選或取消勾選和變更前的數值相比都只會有多一個或少一個的差別
    const clickCarrier = _.get(_.xor(prevCarriers, nextCarriers), '0')
    if (clickCarrier) {
      this.props.sendPageviewByAction({
        type: GA_CLICK_ACTION.DAILY_CARRIERS,
        payload: clickCarrier
      })
    }
  }

  exportExcel = async () => {
    const {
      openExportModal,
      closeExportModal
    } = this.props

    this.props.sendPageviewByAction({
      type: GA_CLICK_ACTION.DAILY_EXPORT_EXCEL
    })
    openExportModal()

    const { fetchAllListingPageViews, formValues } = this.props
    const { listingId, listingType } = formValues
    // omit comparisonRange options which not be supported on excelExport
    const paramsList = getPageViewsParamsList(_.omit(formValues, 'comparisonRange'))

    const [reqError, respList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (reqError) {
      closeExportModal({ isError: true })
      return
    }

    const data = _.get(respList, '[0].data', [])
    const ExportHelper = new ExcelExportHelper({
      headers: {
        reportDate: '日期',
        carrier: '裝置',
        pageView: '瀏覽量',
        ...(listingType === 'order' ? { listingId: '賣場編號' } : {})
      },
      fileName: 'pageviewbyday_{{ nowDate.format("YYYYMMDDHHmmss") }}',
      bookType: 'xlsx'
    })
    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName(),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(transformToSheetData(data, listingId))
        }
      ]
    }
    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }

  render () {
    const {
      isLoading,
      isComparisonChart,
      isDailyDetailLoaded,
      isDetailVisible,
      timeDuration,
      chartDataList,
      viewsSummariesList,
      saleStateMapByTimeDuration,
      noDataDescription,
      isOneCarrierSelected,
      researchedFormData
    } = this.state
    const { isSeller, onlineDate, flags } = this.props
    const isAllCarrierSelected = _.isEmpty(researchedFormData)
      ? true
      : _.isEqual(_.get(researchedFormData, SEARCH_FIELD.CARRIERS), [CARRIER.ALL])
    const disableExportBtn = isLoading
    const isPVGA = getFeatureToggle(flags, 'sp_PV_GA')
    return (
      <Loader loaded={!isLoading}>
        <Block>
          <SearchForm
            onFormInit={this.setSearchFormProps}
            onSubmit={this.onSearch}
            onCollapseChange={this.onCollapseChange}
            onListingChange={this.onListingChange}
            onCarriersChange={this.onCarriersChange}
            onExportExcel={this.exportExcel}
            disableExportBtn={disableExportBtn}
            isSeller={isSeller}
            onlineDate={onlineDate}
          />
        </Block>
        {!isSeller && isPVGA && (
          <div className={reminderStyles.DashboardReminder}>
            <p>* 瀏覽量(PV)數據自 2026/01/13(含)起，改由 Google Analytics (GA) 提供。</p>
            <p>* 2026/01/13 以前的瀏覽量數據，則維持來自原有的 Yahoo 內部系統統計。</p>
          </div>
        )}
        <Block
          data-role='chart-area'
          noMarginBottom
        >
          {_.isEmpty(chartDataList)
            ? (
              <NoData description={noDataDescription} />
            )
            : (
              <>
                {
                  viewsSummariesList.map(({ title, summaries }, i) => (
                    <SummaryBanner
                      key={i}
                      className={styles.summaryBanner}
                      title={title}
                      summaries={summaries}
                      inline
                    />
                  ))
                }
                <ViewsChart
                  data={chartDataList}
                  isComparison={isComparisonChart}
                  timeDuration={timeDuration}
                  onTimeDurationChange={this.onTimeDurationChange}
                  summaries={viewsSummariesList}
                  isDetailVisible={isDetailVisible}
                  isDailyDetailLoaded={isDailyDetailLoaded}
                  saleStateMapByTimeDuration={saleStateMapByTimeDuration}
                  isSeller={isSeller}
                  isOneCarrierSelected={isOneCarrierSelected}
                  isAllCarrierSelected={isAllCarrierSelected}
                />
              </>
            )
          }
        </Block>
      </Loader>
    )
  }
}

const mapStateToProps = state => ({
  formValues: _.get(state, `form.${SEARCH_CONFIG.form}.values`)
})

export default withConnectedExport(connect(mapStateToProps)(PageViewsDaily))
